import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '../../../ui/Input/Input';
import { FieldShell } from './FieldShell';
import type { RhfFieldBaseProps } from './types';

export interface RhfTextFieldProps extends RhfFieldBaseProps {
  /** Override the input `type` attribute (default `'text'`). */
  type?: string;
  /**
   * `inputMode` hint for mobile keyboards — IBAN / tax id fields
   * pass `'numeric'` so the digit pad opens. Has no effect on
   * desktop browsers.
   */
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
  /** Always rendered LTR regardless of document direction (IBAN). */
  forceLtr?: boolean;
  /** Auto-uppercase on change (IBAN). */
  uppercase?: boolean;
  /**
   * Transform raw input before writing it back to the RHF value.
   * Use for trim-on-blur, prefix stripping, etc. Runs on every
   * keystroke.
   */
  transform?: (value: string) => string;
  /**
   * Read-only flag passed through to the input. Does NOT skip
   * rendering (we still want the label + value visible).
   */
  readOnly?: boolean;
  /** Max length on the underlying `<input>`. */
  maxLength?: number;
  /** Optional fixed prefix rendered inside the input (e.g. `#`). */
  prefix?: string;
}

/**
 * `RhfTextField` — RHF-aware single-line text input that the
 * domain field components (`DisplayNameField`, `IbanField`, etc.)
 * pre-configure with their own label / placeholder / helper.
 *
 * Why a thin custom wrapper instead of leaning on `CustomFormField`?
 *   - `CustomFormField` requires the consumer to pass `control` —
 *     these fields read from `useFormContext()` so the call-sites
 *     stay one-liners.
 *   - `CustomFormField` lives under a `<Form />` wrapper from the
 *     `ui/Form` primitive (which itself wraps `<FormProvider />`).
 *     Some of the new tabbed surfaces want bare RHF + a Save
 *     button per section without the extra `<Form />` wrapper.
 *     Keeping the field independent of `<Form>` keeps it portable.
 *   - Direct `<Controller>` access lets us own the helper / error
 *     placement so `FieldShell` is the single source of truth.
 */
export function RhfTextField({
  name,
  label,
  placeholder,
  helper,
  required,
  financing,
  logisti,
  disabled,
  className,
  type = 'text',
  inputMode,
  forceLtr,
  uppercase,
  transform,
  readOnly,
  maxLength,
  prefix,
}: RhfTextFieldProps & { label: string }) {
  const { control } = useFormContext();
  const generatedId = useId();
  const inputId = `field-${name}-${generatedId}`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const handleChange = (raw: string) => {
          let next = raw;
          if (uppercase) next = next.toUpperCase();
          if (transform) next = transform(next);
          field.onChange(next);
        };

        // Treat a non-empty string as "filled". Whitespace-only
        // doesn't satisfy the requirement — the lender wants real
        // data, not a smear of spaces.
        const satisfied =
          typeof field.value === 'string' && field.value.trim().length > 0;

        return (
          <FieldShell
            label={label}
            helper={helper}
            required={required}
            financing={financing}
            logisti={logisti}
            financingSatisfied={satisfied}
            error={fieldState.error?.message}
            htmlFor={inputId}
            className={className}>
            {prefix ? (
              <div className="flex h-10 w-full overflow-hidden rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <span className="flex items-center border-e border-input bg-muted px-3 text-sm text-muted-foreground">
                  {prefix}
                </span>
                <input
                  id={inputId}
                  type={type}
                  inputMode={inputMode}
                  placeholder={placeholder}
                  value={(field.value as string | undefined) ?? ''}
                  onChange={e => handleChange(e.target.value)}
                  onBlur={field.onBlur}
                  disabled={disabled}
                  readOnly={readOnly}
                  maxLength={maxLength}
                  className="w-full bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  style={forceLtr ? { direction: 'ltr', textAlign: 'left' } : undefined}
                />
              </div>
            ) : (
              <Input
                id={inputId}
                type={type}
                inputMode={inputMode}
                placeholder={placeholder}
                value={(field.value as string | undefined) ?? ''}
                onChange={e => handleChange(e.target.value)}
                onBlur={field.onBlur}
                disabled={disabled}
                readOnly={readOnly}
                maxLength={maxLength}
                style={forceLtr ? { direction: 'ltr', textAlign: 'left' } : undefined}
              />
            )}
          </FieldShell>
        );
      }}
    />
  );
}
